<script setup>
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import StatusChip from '@/components/shared/StatusChip.vue'
import {
  notificationPurposeLabel,
  useNotificationTemplatesStore,
} from '@/stores/notificationTemplates'

definePage({
  meta: {
    navActiveLink: 'notification-templates',
  },
})

const route = useRoute()
const store = useNotificationTemplatesStore()
const { t } = useI18n()

const formError = ref('')
const formSuccess = ref('')
const activateDialog = ref(false)
const archiveDialog = ref(false)
const insertTarget = ref('body')

const form = ref({
  name: '',
  subject: '',
  body: '',
})

const template = computed(() => store.current)
const isArchived = computed(() => template.value?.lifecycle_state === 'archived')
const isDraft = computed(() => template.value?.lifecycle_state === 'draft')
const isActive = computed(() => template.value?.lifecycle_state === 'active')
const canEdit = computed(() => !isArchived.value)
const placeholders = computed(() => template.value?.placeholders || [])

const syncForm = () => {
  const current = store.current
  if (!current)
    return
  form.value = {
    name: current.name || '',
    subject: current.subject || '',
    body: current.body || '',
  }
}

const load = async () => {
  formError.value = ''
  await store.fetchNotificationTemplate(route.params.id)
  syncForm()
}

onMounted(() => {
  load().catch(() => null)
})

const save = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.updateNotificationTemplate(route.params.id, { ...form.value })
    syncForm()
    formSuccess.value = t('notificationTemplates.saved')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const confirmActivate = async () => {
  formError.value = ''
  try {
    await store.activateNotificationTemplate(route.params.id)
    activateDialog.value = false
    syncForm()
    formSuccess.value = t('notificationTemplates.activated')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const confirmArchive = async () => {
  formError.value = ''
  try {
    await store.archiveNotificationTemplate(route.params.id)
    archiveDialog.value = false
    syncForm()
    formSuccess.value = t('notificationTemplates.archived')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const markInsertTarget = target => {
  insertTarget.value = target
}

const insertPlaceholder = token => {
  const snippet = `{{${token}}}`
  const field = insertTarget.value === 'subject' ? 'subject' : 'body'

  form.value[field] = `${form.value[field] || ''}${snippet}`
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center gap-2 mb-4">
      <VBtn
        variant="text"
        prepend-icon="ri-arrow-left-line"
        :to="{ name: 'notification-templates' }"
      >
        {{ $t('notificationTemplates.back') }}
      </VBtn>
    </div>

    <div
      v-if="store.loading && !template"
      role="status"
    >
      {{ $t('notificationTemplates.loading') }}
    </div>

    <VAlert
      v-else-if="!template && store.error"
      type="error"
      variant="tonal"
      role="alert"
    >
      {{ store.error }}
    </VAlert>

    <template v-else-if="template">
      <div class="d-flex flex-wrap align-start justify-space-between gap-4 mb-6">
        <div>
          <h1 class="text-h4 mb-1">
            {{ template.name }}
          </h1>
          <p class="text-medium-emphasis mb-2">
            {{ notificationPurposeLabel(template.purpose) }}
          </p>
          <StatusChip
            domain="notification_template"
            :state="template.lifecycle_state"
          />
        </div>
        <div class="d-flex flex-wrap gap-2">
          <VBtn
            v-if="isDraft"
            color="primary"
            :loading="store.saving"
            @click="activateDialog = true"
          >
            {{ $t('notificationTemplates.activate') }}
          </VBtn>
          <VBtn
            v-if="isActive || isDraft"
            variant="tonal"
            color="secondary"
            :loading="store.saving"
            @click="archiveDialog = true"
          >
            {{ $t('notificationTemplates.archive') }}
          </VBtn>
        </div>
      </div>

      <VAlert
        v-if="formSuccess"
        type="success"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ formSuccess }}
      </VAlert>
      <VAlert
        v-if="formError"
        type="error"
        variant="tonal"
        class="mb-4"
        role="alert"
      >
        {{ formError }}
      </VAlert>

      <VCard
        flat
        border
      >
        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
            role="status"
          >
            {{ $t('notificationTemplates.editHint') }}
          </VAlert>

          <VTextField
            v-model="form.name"
            :label="$t('notificationTemplates.fields.name')"
            :disabled="!canEdit"
            class="mb-4"
          />

          <div class="mb-2">
            <span class="text-caption text-medium-emphasis">
              {{ $t('notificationTemplates.placeholdersLabel') }}
            </span>
            <div class="d-flex flex-wrap gap-2 mt-2">
              <VBtn
                v-for="token in placeholders"
                :key="token"
                size="small"
                variant="tonal"
                :aria-label="$t('notificationTemplates.insertPlaceholder', {
                  label: $t(`notificationTemplates.placeholders.${token}`),
                })"
                @click="insertPlaceholder(token)"
              >
                {{ $t(`notificationTemplates.placeholders.${token}`) }}
              </VBtn>
            </div>
          </div>

          <VTextField
            v-model="form.subject"
            :label="$t('notificationTemplates.fields.subject')"
            :disabled="!canEdit"
            class="mb-4 mt-4"
            @focus="markInsertTarget('subject')"
          />
          <VTextarea
            v-model="form.body"
            :label="$t('notificationTemplates.fields.body')"
            :disabled="!canEdit"
            rows="8"
            @focus="markInsertTarget('body')"
          />

          <div
            v-if="canEdit"
            class="d-flex justify-end mt-4"
          >
            <VBtn
              color="primary"
              :loading="store.saving"
              @click="save"
            >
              {{ $t('common.save') }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </template>

    <ConfirmDialog
      v-model="activateDialog"
      :title="$t('notificationTemplates.activateTitle')"
      :body="$t('notificationTemplates.activateBody')"
      :confirm-label="$t('notificationTemplates.activate')"
      :loading="store.saving"
      @confirm="confirmActivate"
    />

    <ConfirmDialog
      v-model="archiveDialog"
      :title="$t('notificationTemplates.archiveTitle')"
      :body="$t('notificationTemplates.archiveBody')"
      :confirm-label="$t('notificationTemplates.archive')"
      :loading="store.saving"
      @confirm="confirmArchive"
    />
  </div>
</template>
